import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { pmsFrom } from '@/integrations/pms';
import { SEOHead } from '@/components/seo/SEOHead';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, Search, Star, DollarSign, ArrowRight } from 'lucide-react';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { usePageDesign } from '@/hooks/usePageDesign';

const ProductsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('products');

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['public-products'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('products')
        .select('*')
        .eq('is_active', true)
        .order('is_featured', { ascending: false })
        .order('sort_order');
      if (error) throw error;
      return data;
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['public-product-categories'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('product_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
  });

  const filtered = products.filter(p => {
    const matchesSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.brand?.toLowerCase().includes(search.toLowerCase());
    const matchesCat = categoryFilter === 'all' || p.category_id === categoryFilter;
    return matchesSearch && matchesCat;
  });

  return (
    <>
      <SEOHead title="Products" description="Browse our selection of quality equipment and products." />
      <div className="min-h-screen">
        <ConfigurableHero
          heroStyle={heroStyle}
          title="Our Products"
          subtitle="Browse our selection of quality equipment, professionally installed and backed by warranty."
          badge="Shop"
          showBreadcrumbs={showBreadcrumbs}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Products' }]}
          showTrustBadges={showTrustBadges}
          showCtaButtons={showCtaButtons}
        />

        {/* Filters */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Badge variant="secondary" className="whitespace-nowrap">{filtered.length} products</Badge>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          {isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map(i => <div key={i} className="h-80 rounded-xl bg-muted animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg">No products found.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map(p => {
                const images = (p.images as string[] | null) || [];
                const cat = categories.find(c => c.id === p.category_id);
                return (
                  <Link
                    key={p.id}
                    to={`/products/${p.slug}`}
                    className="group rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
                  >
                    <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                      {images[0] ? (
                        <img src={images[0]} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center"><Package className="h-10 w-10 text-muted-foreground/30" /></div>
                      )}
                      {p.is_featured && (
                        <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground gap-1"><Star className="h-3 w-3 fill-current" />Featured</Badge>
                      )}
                      {!p.in_stock && (
                        <Badge variant="destructive" className="absolute top-3 right-3">Out of Stock</Badge>
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      {cat && <span className="text-xs text-primary font-medium mb-1">{cat.name}</span>}
                      <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-1">{p.name}</h3>
                      {p.brand && <span className="text-xs text-muted-foreground mb-2">{p.brand}{p.model_number ? ` • ${p.model_number}` : ''}</span>}
                      {p.short_description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{p.short_description}</p>}
                      <div className="mt-auto flex items-center justify-between">
                        <div>
                          {p.price != null && (
                            <div className="flex items-baseline gap-2">
                              <span className="text-xl font-bold text-foreground">${Number(p.price).toLocaleString()}</span>
                              {p.compare_at_price != null && (
                                <span className="text-sm text-muted-foreground line-through">${Number(p.compare_at_price).toLocaleString()}</span>
                              )}
                            </div>
                          )}
                          {p.financing_available && p.monthly_price != null && (
                            <p className="text-xs text-primary mt-0.5">or ${Number(p.monthly_price)}/mo with financing</p>
                          )}
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductsPage;
